/**
 * Making authored HTML safe to put on the page.
 *
 * Text sections hold HTML: the editor produces it, and importing a Word
 * document produces a great deal of it. That HTML is written into the document
 * with `v-html`, which does no escaping, so anything dangerous in it runs.
 *
 * The threat is real rather than theoretical. A report is shared with
 * colleagues, and the person who wrote a section is not always the person
 * reading it. A crafted `.docx` is an attachment somebody will open.
 *
 * The approach is an **allowlist**: parse the markup, keep the elements and
 * attributes a report legitimately needs, and drop everything else. A
 * denylist of dangerous tags is the wrong shape, because it has to anticipate
 * every attack, and it only takes one it did not.
 *
 * Parsing happens in an inert document, so nothing loads or executes while
 * this is deciding what to keep.
 */

/** Elements a report's prose may contain. */
const ALLOWED_ELEMENTS: ReadonlySet<string> = new Set([
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'sub',
  'sup',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'a',
  'span',
  'div',
  'hr',
])

/** Attributes each element may keep. Everything else is dropped. */
const ALLOWED_ATTRIBUTES: Readonly<Record<string, readonly string[]>> = {
  a: ['href', 'title'],
  th: ['colspan', 'rowspan'],
  td: ['colspan', 'rowspan'],
  span: ['class'],
  div: ['class'],
  p: ['class'],
}

/**
 * URL schemes a link may use.
 *
 * `javascript:` is the obvious exclusion. `data:` is the less obvious one: a
 * `data:text/html` link runs script in this origin when clicked.
 */
const ALLOWED_SCHEMES: ReadonlySet<string> = new Set(['http:', 'https:', 'mailto:', 'tel:'])

function isSafeLink(value: string): boolean {
  const trimmed = value.trim()
  // A relative or anchor link has no scheme and cannot name another origin.
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) return true
  try {
    return ALLOWED_SCHEMES.has(new URL(trimmed, window.location.origin).protocol)
  } catch {
    return false
  }
}

/**
 * The markup, with anything unsafe removed.
 *
 * Disallowed elements are **unwrapped rather than deleted**: their text
 * survives in place. Deleting them would silently lose a paragraph somebody
 * wrote because it happened to be inside a tag this does not recognise, and
 * losing an author's words to protect them is a poor trade.
 *
 * `<script>` and `<style>` are the exception: their contents are code, not
 * prose, and unwrapping them would paste the code into the document as text.
 */
export function sanitiseHtml(html: string): string {
  if (!html.trim()) return ''

  // An inert document: nothing here loads, requests or runs.
  const parsed = new DOMParser().parseFromString(html, 'text/html')

  clean(parsed.body)

  return parsed.body.innerHTML
}

function clean(parent: Element): void {
  // A copy, because the loop moves and removes nodes as it goes.
  for (const node of [...parent.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE) continue

    if (node.nodeType !== Node.ELEMENT_NODE) {
      // Comments and processing instructions carry nothing a report needs.
      node.remove()
      continue
    }

    const element = node as Element
    const tag = element.tagName.toLowerCase()

    if (tag === 'script' || tag === 'style' || tag === 'template') {
      element.remove()
      continue
    }

    if (!ALLOWED_ELEMENTS.has(tag)) {
      // Clean what is inside first, then lift it into place.
      clean(element)
      element.replaceWith(...element.childNodes)
      continue
    }

    stripAttributes(element, tag)
    clean(element)
  }
}

function stripAttributes(element: Element, tag: string): void {
  const permitted = ALLOWED_ATTRIBUTES[tag] ?? []

  for (const attribute of [...element.attributes]) {
    const name = attribute.name.toLowerCase()

    // Every `on*` handler, whatever it is called. This is what would run.
    if (!permitted.includes(name)) {
      element.removeAttribute(attribute.name)
      continue
    }

    if (name === 'href' && !isSafeLink(attribute.value)) {
      element.removeAttribute(attribute.name)
    }
  }

  // A link that leaves the application opens in a new tab, and must not be
  // able to reach back through `window.opener` to redirect this one.
  if (tag === 'a' && element.getAttribute('href')) {
    element.setAttribute('rel', 'noopener noreferrer')
    element.setAttribute('target', '_blank')
  }
}

/** The words, with the markup taken off. For counting and for summaries. */
export function textOf(html: string): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  return (parsed.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/** Whether a text section has anything in it. */
export function isEmptyHtml(html: string): boolean {
  return textOf(html).length === 0
}
